import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProviderBrandListSortCol, TProviderBrandList, TProviderBrandListResponse, TProviderBrandListQuery, TProviderBrandListSortCol, adaptProviderBrandFromListResponse, adaptProviderBrandListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_PROVIDER_BRAND } from '../../backend-paths';


export const providerBrandListGetApi = createAsyncThunk(
	BACKEND_ADMIN_PROVIDER_BRAND,
	(payload: TProviderBrandListQuery | undefined, thunkApi) => {
		const params = adaptProviderBrandListQueryToRequest(payload);

		return HttpService.get<TProviderBrandListResponse>(BACKEND_ADMIN_PROVIDER_BRAND, { params }).then(result => {
			return adaptProviderBrandFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProviderBrandList;
	query: TProviderBrandListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProviderBrandListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const providerBrandListSlice = createSlice({
	name: 'providerBrandListSlice',
	initialState,
	reducers: {
		setProviderBrandListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProviderBrandListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProviderBrandListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProviderBrandListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProviderBrandListQuerySortCol(state, action: PayloadAction<TProviderBrandListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProviderBrandList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[providerBrandListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[providerBrandListGetApi.fulfilled.type]: (state, action: PayloadAction<TProviderBrandList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[providerBrandListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProviderBrandListSelector = () => useAppSelector(state => state[providerBrandListSlice.name]);

export const { clearProviderBrandList, setProviderBrandListQueryLimit, setProviderBrandListQueryOffset, setProviderBrandListQuerySort, setProviderBrandListQuerySortCol, setProviderBrandListQuerySearch } = providerBrandListSlice.actions;
